import React, { useRef, useState } from 'react';
import { C } from './UI';
import { downscaleImage } from '../utils/downscaleImage';

interface EstimateResult {
    estimatedUnits: number;
    description: string;
    suggestedPrice: number;
}

interface Props {
    serviceType: string;
    onEstimate: (result: EstimateResult, base64: string) => void;
}

const API = process.env.REACT_APP_API_URL || 'http://10.0.0.114:5000';

export function PhotoUpload({ serviceType, onEstimate }: Props) {
    const fileRef = useRef<HTMLInputElement>(null);
    const [preview, setPreview] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [result, setResult] = useState<EstimateResult | null>(null);

    const handleFile = async (file: File) => {
        if (!file.type.startsWith('image/')) {
            setError('Please select an image file.');
            return;
        }
        if (file.size > 10 * 1024 * 1024) {
            setError('Image too large. Please use a photo under 10MB.');
            return;
        }

        setError('');
        setResult(null);

        try {
            const { dataUrl, base64 } = await downscaleImage(file);
            setPreview(dataUrl);
            await analyzeImage(base64);
        } catch {
            setError('Could not read that photo — please try again or pick a different one.');
        }
    };

    const analyzeImage = async (base64: string) => {
        setLoading(true);
        try {
            const token = localStorage.getItem('br_token');
            const res = await fetch(`${API}/api/estimate`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ base64Image: base64, serviceType })
            });

            if (!res.ok) throw new Error('Could not analyze image');

            const data: EstimateResult = await res.json();
            setResult(data);
            onEstimate(data, base64);
        } catch (e: any) {
            setError('Could not analyze image. You can still enter the quantity manually.');
        } finally {
            setLoading(false);
        }
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        const file = e.dataTransfer.files[0];
        if (file) handleFile(file);
    };

    const reset = () => {
        setPreview(null);
        setResult(null);
        setError('');
        if (fileRef.current) fileRef.current.value = '';
    };

    return (
        <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: C.textSub, marginBottom: 5 }}>
                Photo of your pile{' '}
                <span style={{ color: C.textMuted, fontWeight: 400 }}>(optional — AI will count for you)</span>
            </label>

            {!preview ? (
                <div
                    onDrop={handleDrop}
                    onDragOver={e => e.preventDefault()}
                    onClick={() => fileRef.current?.click()}
                    style={{
                        border: '2px dashed var(--color-primary, #5DCAA5)',
                        borderRadius: 10,
                        padding: '28px 16px',
                        textAlign: 'center',
                        cursor: 'pointer',
                        background: C.greenBg,
                    }}
                >
                    <div style={{ fontSize: 32, marginBottom: 8 }}>📷</div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--color-primary, #0F6E56)' }}>
                        Tap to take or upload a photo
                    </div>
                    <div style={{ fontSize: 12, color: C.textSub, marginTop: 4 }}>
                        AI will count your boxes and suggest a price
                    </div>
                    <input
                        ref={fileRef}
                        type="file"
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={e => {
                            const file = e.target.files?.[0];
                            if (file) handleFile(file);
                            e.target.value = '';
                        }}
                    />
                </div>
            ) : (
                <div style={{ position: 'relative', borderRadius: 10, overflow: 'hidden', border: `1px solid ${C.border}` }}>
                    <img
                        src={preview}
                        alt="Pickup pile"
                        style={{ width: '100%', height: 200, objectFit: 'cover', display: 'block' }}
                    />

                    {/* Loading overlay */}
                    {loading && (
                        <div style={{
                            position: 'absolute', inset: 0,
                            background: 'rgba(15,110,86,0.88)',
                            display: 'flex', flexDirection: 'column',
                            alignItems: 'center', justifyContent: 'center', gap: 10
                        }}>
                            <div style={{
                                width: 28, height: 28,
                                border: '3px solid rgba(255,255,255,0.3)',
                                borderTopColor: '#fff', borderRadius: '50%',
                                animation: 'spin 0.8s linear infinite'
                            }} />
                            <div style={{ color: '#fff', fontSize: 13, fontWeight: 600 }}>
                                AI analyzing your pile...
                            </div>
                            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                        </div>
                    )}

                    {/* Result overlay */}
                    {result && !loading && (
                        <div style={{
                            position: 'absolute', bottom: 0, left: 0, right: 0,
                            background: 'rgba(15,110,86,0.92)',
                            padding: '10px 14px',
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                        }}>
                            <div>
                                <div style={{ color: '#fff', fontSize: 13, fontWeight: 600 }}>
                                    AI estimate: ~{result.estimatedUnits} boxes
                                </div>
                                <div style={{ color: '#9FE1CB', fontSize: 11, marginTop: 2 }}>
                                    {result.description}
                                </div>
                            </div>
                            <div style={{ color: '#9FE1CB', fontSize: 16, fontWeight: 700 }}>
                                ${result.suggestedPrice.toFixed(2)}
                            </div>
                        </div>
                    )}

                    {/* Remove button */}
                    <button
                        onClick={reset}
                        style={{
                            position: 'absolute', top: 8, right: 8,
                            background: 'rgba(0,0,0,0.55)', border: 'none',
                            borderRadius: '50%', width: 28, height: 28,
                            color: '#fff', cursor: 'pointer', fontSize: 14,
                            display: 'flex', alignItems: 'center', justifyContent: 'center'
                        }}
                    >
                        ✕
                    </button>
                </div>
            )}

            {error && (
                <div style={{ fontSize: 12, color: '#9B1C1C', marginTop: 6 }}>{error}</div>
            )}
        </div>
    );
}
